import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import QuoteForm from '../../../components/feature/QuoteForm';
import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function PavingEstimate() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqs = [
    {
      question: 'How much does an asphalt driveway cost in Lincoln, NE?',
      answer: 'Asphalt driveway costs in Lincoln typically range from $3 to $7 per square foot, depending on the size, site preparation, and material quality. A standard 600 sq ft driveway costs between $2,500 and $4,500. Factors affecting price include excavation needs, base material, thickness, and accessibility.'
    },
    {
      question: 'What factors affect my paving estimate?',
      answer: 'Key factors include: driveway size (square footage), site preparation requirements, base material quality, asphalt thickness (typically 2-3 inches), drainage needs, existing surface removal, accessibility for equipment, and any special features like borders or decorative elements.'
    },
    {
      question: 'How long does it take to get a paving estimate?',
      answer: 'We provide free estimates within 24 hours. Our team will visit your Lincoln property, measure the area, assess site conditions, and provide a detailed written quote with no obligation. Most on-site evaluations take 30-45 minutes.'
    },
    {
      question: 'Is concrete or asphalt cheaper for driveways?',
      answer: 'Asphalt is typically 30-40% less expensive than concrete upfront. In Lincoln, asphalt driveways cost $3-7 per square foot, while concrete runs $6-12 per square foot. However, both materials have different maintenance needs and lifespans to consider.'
    },
    {
      question: 'Do you offer financing for paving projects?',
      answer: 'Yes, we offer flexible financing options for qualified customers. We work with several lenders to provide competitive rates and payment plans that fit your budget. Ask about financing when you request your free estimate.'
    }
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 sm:pb-20">
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/86a780de6eaa32b651771846558713dc.jpeg"
            alt="Lincoln NE paving estimate services"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <p className="text-primary-500 text-xs sm:text-sm mb-3 sm:mb-4 tracking-wider">Home / Services / Paving Estimate</p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black mb-4 sm:mb-6 leading-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            LINCOLN NE PAVING ESTIMATE
          </h1>
          <p className="text-base sm:text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-6 sm:mb-8 px-4">
            Professional asphalt driveway estimates, pavement cost calculations, and free paving quotes in Lincoln, Nebraska
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <a href="#quote-form" className="bg-primary-600 text-white px-6 sm:px-8 py-4 rounded-lg font-semibold hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer text-base sm:text-lg">
              GET FREE QUOTE
            </a>
            <a href="tel:4025550123" className="bg-white/10 backdrop-blur-sm text-white border-2 border-white px-6 sm:px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer text-base sm:text-lg">
              CALL (402) 555-0123
            </a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Sidebar */}
            <div className="lg:col-span-1 order-2 lg:order-1">
              <div className="lg:sticky lg:top-24 space-y-6">
                <div className="bg-gray-50 rounded-xl p-5 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-bold mb-4 text-gray-900">Related Services</h3>
                  <div className="space-y-3">
                    <Link to="/services/asphalt-repair" className="block text-primary-600 hover:text-primary-700 font-medium text-sm sm:text-base py-2">→ Asphalt Repair</Link>
                    <Link to="/services/concrete-driveway" className="block text-primary-600 hover:text-primary-700 font-medium text-sm sm:text-base py-2">→ Concrete Driveway</Link>
                    <Link to="/services/sealcoating" className="block text-primary-600 hover:text-primary-700 font-medium text-sm sm:text-base py-2">→ Sealcoating</Link>
                    <Link to="/services/costs-estimates" className="block text-primary-600 hover:text-primary-700 font-medium text-sm sm:text-base py-2">→ Costs & Estimates</Link>
                  </div>
                </div>

                <div className="bg-primary-600 text-white rounded-xl p-5 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-bold mb-2">Need Help?</h3>
                  <p className="mb-4 text-orange-100 text-sm sm:text-base">Call us for immediate assistance</p>
                  <a href="tel:3084705637" className="block bg-white text-primary-600 text-center py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors cursor-pointer text-base sm:text-lg">
                    (308) 470-5637
                  </a>
                </div>

                <div id="quote-form">
                  <QuoteForm variant="light" />
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-2 order-1 lg:order-2">
              <article>
                <h2 className="text-3xl sm:text-4xl font-bold mb-4 sm:mb-6 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Professional Paving Estimates in Lincoln, Nebraska
                </h2>
                
                <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-4 sm:mb-6">
                  When you're planning an <strong>asphalt driveway installation</strong> or <strong>pavement project in Lincoln, NE</strong>, getting an accurate estimate is the crucial first step. Our experienced team provides detailed, transparent paving estimates that help you understand exactly what your project will cost and what's included. We've completed over 500 paving projects throughout Lancaster County, and we know how to deliver fair, competitive pricing without sacrificing quality.
                </p>

                <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-4 sm:mb-6">
                  Understanding <strong>Lincoln NE asphalt driveway costs</strong> can be confusing with so many variables involved. That's why we take the time to visit your property, assess site conditions, measure accurately, and explain every line item in your estimate. Whether you need a residential driveway, commercial parking lot, or pavement repair, we provide free quotes with no obligation or pressure.
                </p>

                <div className="bg-gray-50 rounded-xl p-6 sm:p-8 my-6 sm:my-8">
                  <h3 className="text-xl sm:text-2xl font-bold mb-4 text-gray-900">What's Included in Our Paving Estimates</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-primary-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                      <span className="text-gray-700 text-sm sm:text-base">Detailed site measurement and square footage calculation</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-primary-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                      <span className="text-gray-700 text-sm sm:text-base">Site preparation and excavation requirements</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-primary-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                      <span className="text-gray-700 text-sm sm:text-base">Base material type and depth specifications</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-primary-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                      <span className="text-gray-700 text-sm sm:text-base">Asphalt or concrete thickness and quality grade</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-primary-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                      <span className="text-gray-700 text-sm sm:text-base">Drainage solutions and grading work</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-primary-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                      <span className="text-gray-700 text-sm sm:text-base">Timeline and project completion schedule</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-primary-600 text-xl mr-3 mt-1 flex-shrink-0"></i>
                      <span className="text-gray-700 text-sm sm:text-base">Warranty information and maintenance recommendations</span>
                    </li>
                  </ul>
                </div>

                <h3 className="text-2xl sm:text-3xl font-bold mb-4 text-gray-900 mt-8 sm:mt-12">Lincoln NE Asphalt Driveway Cost Breakdown</h3>
                
                <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-4 sm:mb-6">
                  The average <strong>asphalt driveway cost in Lincoln</strong> ranges from $2,500 to $8,000 for a standard residential driveway, depending on size and complexity. Here's what affects your <strong>pavement estimate</strong>:
                </p>

                <div className="overflow-x-auto mb-6 sm:mb-8 -mx-4 sm:mx-0">
                  <div className="inline-block min-w-full align-middle px-4 sm:px-0">
                    <table className="w-full border-collapse border border-gray-300 text-sm sm:text-base">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border border-gray-300 px-3 sm:px-4 py-3 text-left font-bold">Project Type</th>
                          <th className="border border-gray-300 px-3 sm:px-4 py-3 text-left font-bold">Size Range</th>
                          <th className="border border-gray-300 px-3 sm:px-4 py-3 text-left font-bold">Cost Range</th>
                          <th className="border border-gray-300 px-3 sm:px-4 py-3 text-left font-bold">Per Sq Ft</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-gray-300 px-3 sm:px-4 py-3">Small Driveway</td>
                          <td className="border border-gray-300 px-3 sm:px-4 py-3">300-500 sq ft</td>
                          <td className="border border-gray-300 px-3 sm:px-4 py-3">$1,500-$3,500</td>
                          <td className="border border-gray-300 px-3 sm:px-4 py-3">$5-$7</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border border-gray-300 px-3 sm:px-4 py-3">Standard Driveway</td>
                          <td className="border border-gray-300 px-3 sm:px-4 py-3">500-800 sq ft</td>
                          <td className="border border-gray-300 px-3 sm:px-4 py-3">$2,500-$5,600</td>
                          <td className="border border-gray-300 px-3 sm:px-4 py-3">$5-$7</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 px-3 sm:px-4 py-3">Large Driveway</td>
                          <td className="border border-gray-300 px-3 sm:px-4 py-3">800-1,200 sq ft</td>
                          <td className="border border-gray-300 px-3 sm:px-4 py-3">$4,000-$8,400</td>
                          <td className="border border-gray-300 px-3 sm:px-4 py-3">$5-$7</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border border-gray-300 px-3 sm:px-4 py-3">Commercial Lot</td>
                          <td className="border border-gray-300 px-3 sm:px-4 py-3">5,000+ sq ft</td>
                          <td className="border border-gray-300 px-3 sm:px-4 py-3">$15,000-$75,000</td>
                          <td className="border border-gray-300 px-3 sm:px-4 py-3">$3-$6</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <h3 className="text-2xl sm:text-3xl font-bold mb-4 text-gray-900 mt-8 sm:mt-12">Factors That Affect Your Paving Estimate</h3>

                <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-4 sm:mb-6">
                  Several key factors influence the final cost of your <strong>Lincoln NE paving project</strong>. Understanding these variables helps you make informed decisions and budget appropriately:
                </p>

                <div className="space-y-5 sm:space-y-6 mb-6 sm:mb-8">
                  <div>
                    <h4 className="text-lg sm:text-xl font-bold mb-2 text-gray-900">1. Site Preparation Requirements</h4>
                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                      Proper site preparation is critical for a long-lasting driveway. If your property has poor drainage, unstable soil, or requires significant excavation, this will increase costs. We assess soil conditions, grade requirements, and drainage needs to ensure your pavement has a solid foundation.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-lg sm:text-xl font-bold mb-2 text-gray-900">2. Base Material Quality</h4>
                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                      The base layer is the foundation of your driveway. We typically use 4-6 inches of compacted crushed stone or gravel. Higher-quality base materials cost more but provide better stability and prevent settling, cracking, and premature failure.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-lg sm:text-xl font-bold mb-2 text-gray-900">3. Asphalt Thickness and Quality</h4>
                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                      Standard residential driveways use 2-3 inches of hot-mix asphalt. Commercial applications or heavy-use areas may require 3-4 inches. We use premium asphalt mixes designed for Nebraska's climate, with proper binder content to resist cracking in cold winters and softening in hot summers.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-lg sm:text-xl font-bold mb-2 text-gray-900">4. Existing Surface Removal</h4>
                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                      If you have an old driveway that needs removal, this adds to the project cost. Asphalt removal and disposal typically costs $1-$3 per square foot. However, in some cases, we can overlay existing asphalt if it's in good condition, saving you money.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-lg sm:text-xl font-bold mb-2 text-gray-900">5. Driveway Size and Shape</h4>
                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                      Larger driveways benefit from economies of scale, with lower per-square-foot costs. Complex shapes, curves, or multiple parking areas require more labor and material waste, increasing the overall estimate. Simple rectangular driveways are the most cost-effective.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-lg sm:text-xl font-bold mb-2 text-gray-900">6. Accessibility and Site Conditions</h4>
                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                      Easy access for our paving equipment keeps costs down. Tight spaces, steep slopes, or limited access may require smaller equipment or additional labor, affecting your estimate. We evaluate site accessibility during our free on-site visit.
                    </p>
                  </div>
                </div>

                <div className="bg-primary-50 border-l-4 border-primary-600 p-5 sm:p-6 my-6 sm:my-8">
                  <h4 className="text-lg sm:text-xl font-bold mb-2 text-gray-900">💡 Money-Saving Tip</h4>
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                    Schedule your paving project during spring or fall for the best weather conditions and potentially lower prices. Summer is peak season, while winter paving is limited in Nebraska. Planning ahead can save you 10-15% on your total project cost.
                  </p>
                </div>

                <h3 className="text-2xl sm:text-3xl font-bold mb-4 text-gray-900 mt-8 sm:mt-12">Our Paving Estimate Process</h3>

                <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-4 sm:mb-6">
                  Getting a <strong>free paving quote</strong> from us is simple and straightforward. Here's how our estimate process works:
                </p>

                <div className="space-y-4 mb-6 sm:mb-8">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold mr-3 sm:mr-4 text-base sm:text-lg">1</div>
                    <div className="flex-1">
                      <h5 className="font-bold text-gray-900 mb-1 text-base sm:text-lg">Contact Us</h5>
                      <p className="text-gray-700 text-sm sm:text-base">Call (402) 555-0123 or fill out our online form. We'll schedule a convenient time to visit your Lincoln property.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold mr-3 sm:mr-4 text-base sm:text-lg">2</div>
                    <div className="flex-1">
                      <h5 className="font-bold text-gray-900 mb-1 text-base sm:text-lg">On-Site Evaluation</h5>
                      <p className="text-gray-700 text-sm sm:text-base">Our experienced estimator visits your property, measures the area, assesses site conditions, and discusses your needs and preferences.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold mr-3 sm:mr-4 text-base sm:text-lg">3</div>
                    <div className="flex-1">
                      <h5 className="font-bold text-gray-900 mb-1 text-base sm:text-lg">Detailed Quote</h5>
                      <p className="text-gray-700 text-sm sm:text-base">Within 24 hours, you'll receive a comprehensive written estimate with itemized costs, material specifications, and project timeline.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold mr-3 sm:mr-4 text-base sm:text-lg">4</div>
                    <div className="flex-1">
                      <h5 className="font-bold text-gray-900 mb-1 text-base sm:text-lg">Review and Questions</h5>
                      <p className="text-gray-700 text-sm sm:text-base">We review the estimate with you, answer all questions, and discuss any adjustments or alternatives to fit your budget.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold mr-3 sm:mr-4 text-base sm:text-lg">5</div>
                    <div className="flex-1">
                      <h5 className="font-bold text-gray-900 mb-1 text-base sm:text-lg">Project Scheduling</h5>
                      <p className="text-gray-700 text-sm sm:text-base">Once you approve the estimate, we schedule your project and provide a detailed timeline for completion.</p>
                    </div>
                  </div>
                </div>

                <img
                  src="https://public.readdy.ai/ai/img_res/0630742f0b00b0f292dc85b1bf4af558.jpg"
                  alt="Lincoln NE paving estimate consultation"
                  className="w-full h-64 sm:h-80 lg:h-96 object-cover rounded-xl shadow-lg my-6 sm:my-8"
                />

                <h3 className="text-2xl sm:text-3xl font-bold mb-4 text-gray-900 mt-8 sm:mt-12">Why Choose Us for Your Paving Estimate</h3>

                <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-4 sm:mb-6">
                  As Lincoln's most trusted paving contractor, we provide honest, accurate estimates backed by 15+ years of experience. Here's what sets our estimates apart:
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-5 sm:p-6">
                    <i className="ri-file-list-3-line text-2xl sm:text-3xl text-primary-600 mb-3"></i>
                    <h4 className="text-lg sm:text-xl font-bold mb-2 text-gray-900">Detailed Breakdown</h4>
                    <p className="text-gray-700 text-sm sm:text-base">Every estimate includes itemized costs so you know exactly what you're paying for—no hidden fees or surprises.</p>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-5 sm:p-6">
                    <i className="ri-time-line text-2xl sm:text-3xl text-primary-600 mb-3"></i>
                    <h4 className="text-lg sm:text-xl font-bold mb-2 text-gray-900">Fast Turnaround</h4>
                    <p className="text-gray-700 text-sm sm:text-base">We provide written estimates within 24 hours of our site visit, so you can make decisions quickly.</p>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-5 sm:p-6">
                    <i className="ri-shield-check-line text-2xl sm:text-3xl text-primary-600 mb-3"></i>
                    <h4 className="text-lg sm:text-xl font-bold mb-2 text-gray-900">No Obligation</h4>
                    <p className="text-gray-700 text-sm sm:text-base">Our estimates are completely free with no pressure to commit. Take your time to review and compare.</p>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-5 sm:p-6">
                    <i className="ri-price-tag-3-line text-2xl sm:text-3xl text-primary-600 mb-3"></i>
                    <h4 className="text-lg sm:text-xl font-bold mb-2 text-gray-900">Competitive Pricing</h4>
                    <p className="text-gray-700 text-sm sm:text-base">We offer fair, competitive rates without sacrificing quality materials or workmanship.</p>
                  </div>
                </div>

                <h3 className="text-2xl sm:text-3xl font-bold mb-4 text-gray-900 mt-8 sm:mt-12">Frequently Asked Questions</h3>

                <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                  {faqs.map((faq, index) => (
                    <div key={index} className="border-2 border-gray-200 rounded-xl overflow-hidden">
                      <button
                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                        className="w-full px-5 sm:px-6 py-4 sm:py-5 text-left flex justify-between items-center hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        <span className="font-bold text-gray-900 pr-4 text-sm sm:text-base">{faq.question}</span>
                        <i className={`ri-arrow-down-s-line text-xl sm:text-2xl text-primary-600 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`}></i>
                      </button>
                      {openFaq === index && (
                        <div className="px-5 sm:px-6 py-4 bg-gray-50 border-t-2 border-gray-200">
                          <p className="text-gray-700 leading-relaxed text-sm sm:text-base">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white rounded-2xl p-6 sm:p-8 text-center mt-8 sm:mt-12">
                  <h3 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">Ready for Your Free Paving Estimate?</h3>
                  <p className="text-gray-300 text-base sm:text-lg mb-5 sm:mb-6">
                    Get your detailed quote within 24 hours. No obligation, no pressure—just honest pricing from Lincoln's trusted paving experts.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                    <a href="tel:4025550123" className="bg-primary-600 text-white px-6 sm:px-8 py-4 rounded-lg font-semibold hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer text-base sm:text-lg">
                      CALL (402) 555-0123
                    </a>
                    <a href="#quote-form" className="bg-white text-gray-900 px-6 sm:px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer text-base sm:text-lg">
                      REQUEST QUOTE
                    </a>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}